import * as Sentry from '@sentry/react';

import { CaseRequestApiError } from '@/core/api';

interface SubmitErrorContext {
	caseId: string;
	caseKind: string;
	files: File[];
}

/**
 * Reports a failed case request submit with enough context to investigate it:
 * the HTTP status, correlation id and file metadata WITHOUT the file contents.
 * This is what turns "client got an error, no idea why" into an actionable Sentry
 * event (e.g. 403 = CrowdSec AppSec, 400 = already submitted, 5xx = backend).
 *
 * Note: the raw response body is intentionally NOT sent to Sentry (it may carry
 * internal server details). It is only logged to the browser console, which never
 * leaves the developer's machine.
 */
export function reportCaseRequestSubmitError(error: unknown, ctx: SubmitErrorContext) {
	const apiError = error instanceof CaseRequestApiError ? error : null;

	// Fields safe to send to Sentry: ids, status and file metadata only - no file
	// names, no file contents, no user-entered text, no response body.
	const details = {
		caseId: ctx.caseId,
		caseKind: ctx.caseKind,
		fileCount: ctx.files.length,
		fileSizes: ctx.files.map((f) => f.size),
		fileTypes: ctx.files.map((f) => f.type),
		status: apiError?.status ?? null,
		statusText: apiError?.statusText ?? null,
		requestId: apiError?.requestId ?? null,
	};

	// Surface in the browser console too (stays local, never transmitted), including
	// the response body which is deliberately kept out of the Sentry payload.
	// eslint-disable-next-line no-console
	console.error('Case request submit failed', { ...details, responseBody: apiError?.responseBody ?? null }, error);

	Sentry.captureException(error instanceof Error ? error : new Error('Case request submit failed'), {
		tags: {
			feature: 'case-request-submit',
			httpStatus: apiError?.status,
			requestId: apiError?.requestId ?? undefined,
		},
		extra: details,
	});
}
